import React, { useEffect, useMemo, useState } from "react";
import HeaderSales from "./common/HeaderSales";
import Footer from "./common/Footer";
import scanned_qrcode from "../assets/img/scanned_qrcode.png";
import rejected_scan from "../assets/img/total_rejected.png";
import total_star from "../assets/img/total_star.png";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faArrowRight,
  faStar,
  faGift,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import * as Constants from "./Constants/index";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import Oval from "../loader/CircleLoade";
import "./SalesDashboard.css";

const safeNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
};

const formatNumber = (value) => safeNumber(value).toLocaleString("en-IN");

export default function SalesDashboard() {
  const [isVisible, setIsVisible] = useState(false);
  const [topPlumber, setTopPlumber] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [productTrend, setProductTrend] = useState([]);
  const [loading, setLoading] = useState(false);

  const userType = localStorage.getItem("user_type");
  const userName = localStorage.getItem("user_name") || "CERA Admin";
  const brand = localStorage.getItem("brand");
  const language = localStorage.getItem("langauge") || localStorage.getItem("language") || "en";
  const showPolipluz = userType === "HO" || brand === "POLIPLUZ";

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchDashboardData = async () => {
      if (!token) {
        return;
      }

      let topPlumberEndpoint = "";
      if (userType === "SALES") {
        topPlumberEndpoint =
          "user/manage/userBycityId?limit=10&userType=MEMBER&status=APPROVED&page=1&sortBy=totalPoints&sortAsc=false";
      } else if (
        userType === "SH" ||
        userType === "RO" ||
        userType === "HO" ||
        userType === "MK" ||
        userType === "RO-ASSOCIATE"
      ) {
        topPlumberEndpoint =
          "user/manage/userByparentId?limit=10&userType=MEMBER&status=APPROVED&page=1&sortBy=totalPoints&sortAsc=false";
      }

      const commonHeaders = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": language,
        platform: Constants.pt,
        appVersion: Constants.av,
        program: Constants.program,
        company: Constants.company,
      };

      try {
        setLoading(true);
        const requests = [
          topPlumberEndpoint
            ? axios.get(Constants.baseUrl + topPlumberEndpoint, { headers: commonHeaders })
            : Promise.resolve({ status: 200, data: { output: { results: [] } } }),
          axios.get(Constants.baseUrl + "program/dashboard/sales", {
            headers: commonHeaders,
          }),
          axios.post(Constants.baseUrl + "capture/qr/product_report_trend", {}, {
            headers: commonHeaders,
          }),
        ];

        const [topRes, dashboardRes, productRes] = await Promise.all(requests);

        if (topRes?.data?.output?.results) {
          setTopPlumber(topRes.data.output.results);
        } else {
          setTopPlumber([]);
        }

        if (dashboardRes?.status === 201 && dashboardRes.data.output) {
          setDashboard(dashboardRes.data.output);
        } else {
          setDashboard({});
        }

        if (productRes?.status === 201 && productRes.data.code === 10001) {
          setProductTrend(productRes.data.output || []);
        } else {
          setProductTrend([]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [language, userType]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const dashboardSummary = useMemo(() => {
    const members = safeNumber(dashboard.members);
    const regBySales = safeNumber(dashboard.newSales);
    const rejected = safeNumber(dashboard.memberRejected);
    const pendingApproval = safeNumber(dashboard.memberVerified);
    const approvedPlumbers = safeNumber(dashboard.memberApproved);
    const pendingVerification = safeNumber(dashboard.pendingverification);
    const activePlumbers = safeNumber(dashboard.memberActive);
    const inactivePlumbers = safeNumber(dashboard.memberInActive);
    const totalStarsAllocated = safeNumber(dashboard.pointsGiven);
    const totalStarsRedeemed = safeNumber(dashboard.pointsRedeemed);
    const scanTotalCeraa = safeNumber(dashboard.scanTotalCera)
    const pointsGivenCeraa = safeNumber(dashboard.pointsGivenCera)
    const scanSuccesse = safeNumber(dashboard.scanSuccess)
    const scanTotall = safeNumber(dashboard.scanTotal)
    const successfulScans = safeNumber(
      brand === "POLIPLUZ" ? dashboard.scanTotalPulipluz : dashboard.scanSuccess
    );
    const scanTotal = safeNumber(dashboard.scanTotal);
    const scanRejected = safeNumber(dashboard.scanRejected);
    const topTenTotal = topPlumber.reduce(
      (sum, item) => sum + safeNumber(item.totalPoints),
      0
    );

    return {
      members,
      regBySales,
      rejected,
      pendingApproval,
      approvedPlumbers,
      pendingVerification,
      activePlumbers,
      inactivePlumbers,
      totalStarsAllocated,
      totalStarsRedeemed,
      successfulScans,
      scanTotal,
      scanRejected,
      topTenTotal,
      remainingStars: Math.max(totalStarsAllocated - totalStarsRedeemed, 0),
      scanTotalCeraa,
      pointsGivenCeraa,
      scanSuccesse,
      scanTotall
    };
  }, [brand, dashboard, topPlumber]);

  const registrationSegments = useMemo(() => {
    return [
      {
        label: "Reg. by Sales/Mkt Team",
        value: dashboardSummary.regBySales,
        className: "is-dark",
      },
      {
        label: "Approved Plumbers",
        value: dashboardSummary.approvedPlumbers,
        className: "is-mid",
      },
      {
        label: "Total Rejected",
        value: dashboardSummary.rejected,
        className: "is-light",
      },
    ];
  }, [dashboardSummary]);

  const ceraMetrics = [
    {
      label: "Total Code Reg.",
      value: dashboardSummary.scanTotall,
      icon: scanned_qrcode,
    },
    {
      label: "Total Code Rejected",
      value: dashboardSummary.scanRejected,
      icon: rejected_scan,
    },
    // {
    //   label: "Total successful Scanned",
    //   value: dashboardSummary.successfulScans,
    //   icon: scanned_qrcode,
    // },
    // {
    //   label: "Total Stars Allocated",
    //   value: dashboardSummary.totalStarsAllocated,
    //   icon: total_star,
    // },
  ];

  const polipluzMetrics = showPolipluz
    ? [
      // {
      //   label: "Total Code Reg. Polipluz",
      //   value: safeNumber(dashboard.scanTotalPulipluz),
      //   icon: scanned_qrcode,
      // },
      // {
      //   label: "Total Code Rejected Polipluz",
      //   value: safeNumber(dashboard.scanRejectedPulipluz),
      //   icon: rejected_scan,
      // },
      // {
      //   label: "Total Successful Polipluz",
      //   value: safeNumber(dashboard.scanSuccessPulipluz),
      //   icon: scanned_qrcode,
      // },
      // {
      //   label: "Total Star Allocated Polipluz",
      //   value: safeNumber(dashboard.pointsGivenPulipluz),
      //   icon: total_star,
      // },
      {
        label: "Total successful Scanned CERA & Luxe",
        value: dashboardSummary.scanTotalCeraa,
        icon: scanned_qrcode,
      },
      {
        label: "Total Stars Allocated CERA & Luxe",
        value: dashboardSummary.pointsGivenCeraa,
        icon: total_star,
      },
    ]
    : [];


  const polipluzMetrics2 = showPolipluz
    ? [
      // {
      //   label: "Total Code Reg. Polipluz",
      //   value: safeNumber(dashboard.scanTotalPulipluz),
      //   icon: scanned_qrcode,
      // },
      // {
      //   label: "Total Code Rejected Polipluz",
      //   value: safeNumber(dashboard.scanRejectedPulipluz),
      //   icon: rejected_scan,
      // },
      {
        label: "Total Successful Polipluz",
        value: safeNumber(dashboard.scanSuccessPulipluz),
        icon: scanned_qrcode,
      },
      {
        label: "Total Star Allocated Polipluz",
        value: safeNumber(dashboard.pointsGivenPulipluz),
        icon: total_star,
      },
    ]
    : [];

  const starSummaryRows = [
    {
      label: "ALLOCATED",
      value: dashboardSummary.totalStarsAllocated,
      icon: faStar,
    },
    {
      label: "REDEEMED",
      value: dashboardSummary.totalStarsRedeemed,
      icon: faGift,
    },
    {
      label: "BALANCE",
      value: dashboardSummary.remainingStars,
      icon: faWallet,
    },
  ];

  const topPlumberColumns = useMemo(
    () => [topPlumber.slice(0, 5), topPlumber.slice(5, 10)],
    [topPlumber]
  );

  return (
    <>
      {loading ? <Oval /> : null}
      <HeaderSales />
      <section className="blog-area pt182 dashboard-mock-page">
        <div className="container-fluid">
          <div className="dashboard-mock-shell">
            <div className="dashboard-mock-hero">
              <div className="dashboard-mock-hero-copy">
                {/* <h1 style={{ fontWeight: "bolder" }}>Welcome to {userName}</h1> */}
                <div className="dashboard-mock-strip-wrap">
                  <b className="dashboard-mock-strip-title" style={{ fontWeight: "bolder" }}>Total Registrations</b>
                  <div className="dashboard-mock-strip-bar">
                    {registrationSegments.map((segment) => (
                      <div
                        key={segment.label}
                        className={`dashboard-mock-strip-segment ${segment.className}`}
                      >
                        {formatNumber(segment.value)}
                      </div>
                    ))}
                  </div>
                  <div className="dashboard-mock-strip-labels">
                    {registrationSegments.map((segment) => (
                      <b style={{ fontWeight: "800", fontSize: "16px" }} key={segment.label}>{segment.label}</b>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dashboard-mock-hero-stats">
                <article className="dashboard-mock-hero-stat">
                  <strong>{formatNumber(dashboardSummary.members)}</strong>
                  <b style={{ fontWeight: "800", fontSize: "13px" }}>Total Registration</b>
                </article>
                <article className="dashboard-mock-hero-stat">
                  <strong>{formatNumber(dashboardSummary.pendingApproval)}</strong>
                  <b style={{ fontWeight: "800", fontSize: "13px" }}>Pending Approval</b>
                </article>
                <article className="dashboard-mock-hero-stat">
                  <strong>{formatNumber(dashboardSummary.pendingVerification)}</strong>
                  <b style={{ fontWeight: "800", fontSize: "13px" }}>Pending Verification</b>
                </article>
              </div>
            </div>

            <div className={`dashboard-mock-stat-grid ${showPolipluz ? "is-two-col" : "is-one-col"}`}>
              <article className="dashboard-mock-card">
                <h2>OverAll</h2>
                <div className="dashboard-mock-metric-grid">
                  {ceraMetrics.map((metric) => (
                    <div key={metric.label} className="dashboard-mock-metric-item">
                      <img src={metric.icon} alt="" />
                      <div>
                        <span>{metric.label}</span>
                        <strong>{formatNumber(metric.value)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              {showPolipluz ? (
                <article className="dashboard-mock-card">
                  <h2>CERA & Luxe</h2>
                  <div className="dashboard-mock-metric-grid">
                    {polipluzMetrics.map((metric) => (
                      <div key={metric.label} className="dashboard-mock-metric-item">
                        <img src={metric.icon} alt="" />
                        <div>
                          <span>{metric.label}</span>
                          <strong>{formatNumber(metric.value)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ) : null}

              {showPolipluz ? (
                <article className="dashboard-mock-card">
                  <h2>Polipluz</h2>
                  <div className="dashboard-mock-metric-grid">
                    {polipluzMetrics2.map((metric) => (
                      <div key={metric.label} className="dashboard-mock-metric-item">
                        <img src={metric.icon} alt="" />
                        <div>
                          <span>{metric.label}</span>
                          <strong>{formatNumber(metric.value)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ) : null}
            </div>

            <div className="dashboard-mock-chart-grided">


              <article className="dashboard-mock-card dashboard-mock-stars-card">
                <h2>Total Stars</h2>
                <div className="dashboard-mock-stars-list">
                  {starSummaryRows.map((item) => (
                    <div key={item.label} className="dashboard-mock-stars-pill">
                      <div className="dashboard-mock-stars-pill-label">
                        <span className="dashboard-mock-stars-icon">
                          <FontAwesomeIcon icon={item.icon} />
                        </span>
                        <span>{item.label}</span>
                      </div>
                      <strong>{formatNumber(item.value)}</strong>
                    </div>
                  ))}
                </div>
              </article>

              <article className="dashboard-mock-card dashboard-mock-chart-card">
                <h2>Onboard Plumbers Active / Inactive</h2>
                <div className="dashboard-mock-donut-layout">
                  <div className="dashboard-mock-donut-wrap">
                    <DonutChart
                      activeCount={dashboardSummary.activePlumbers}
                      inactiveCount={dashboardSummary.inactivePlumbers}
                    />
                  </div>
                </div>
              </article>
            </div>

            <div className="dashboard-mock-bottom-grid">
              <article className="dashboard-mock-card dashboard-mock-chart-card">
                {/* <h2>Product Category Performs</h2> */}
                <div className="dashboard-mock-chart-body">
                  <BarChart productTrend={productTrend} />
                </div>
              </article>

              <article className="dashboard-mock-card dashboard-mock-topten-card">
                <div className="dashboard-mock-card-head">
                  <h2>Top 10 Plumbers</h2>
                  <button type="button" className="dashboard-mock-arrow">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
                <div className="dashboard-mock-topten-list">
                  {topPlumberColumns.map((column, columnIndex) => (
                    <div key={`column-${columnIndex}`} className="dashboard-mock-topten-column">
                      {column.map((item, index) => {
                        const rank = columnIndex * 5 + index + 1;
                        return (
                          <div key={item.id} className="dashboard-mock-topten-row">
                            <span className="dashboard-mock-topten-rank">{rank}</span>
                            <div className="dashboard-mock-topten-copy">
                              <strong>{item.name}</strong>
                              <span>ID {item.id}</span>
                            </div>
                            <span className="dashboard-mock-topten-score">
                              {formatNumber(item.totalPoints)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
      <Link
        id="scrollUp"
        to=""
        className={`scroll-to-top-button ${isVisible ? "visible" : "hidden"}`}
        style={{ position: "fixed", zIndex: "2147483647" }}
        onClick={scrollToTop}
      >
        <FontAwesomeIcon icon={faChevronUp} />
      </Link>
      <Footer />
    </>
  );
}
